import { useMemo } from 'react';
import { objectToArray } from '../utils/array';
import { before100k, cautelar, closeToEnd, fipe70, fipe70k, fipe100k, score80 } from './useFilter';
import { useSelector } from 'react-redux';
import {
  collectBrandFilterTokensFromPreferenceValue,
  isLikelyBrandGroupPreferenceEntry,
  normalizeMakeForFilterCompare,
} from '../services/buyersMakes';

const invertPreferencesLabel = {
  CAUTELAR_APROVADA: 'cautelar',
  UF: 'uf',
  DISPUTA_TURBO: 'closeToEnd',
  SCORE: 'score80',
  ABAIXO_DA_FIPE: 'fipe70',
  CEM_MIL_KM: 'before100k',
  FIPE_70K: 'fipe70k',
  FIPE_100K: 'fipe100k',
  BRANDS: 'brands',
  YEAR: 'year',
  KM: 'km',
  ACCEPT_AUCTION: 'acceptAuction',
  ACCEPT_AUCTION_TYPE_A: 'acceptAuctionTypeA',
  REJECT_AUCTION: 'rejectAuction',
  ENGINE_WITH_ISSUES: 'engineWithIssues',
  TRANSMISSION_WITH_ISSUES: 'transmissionWithIssues',
  ARMORED_AND_COMMON: 'armoredAndCommon',
  ONLY_ARMORED: 'onlyArmored',
  NO_ARMORED: 'noArmored',
};

export const usePreferences = () => {
  const { preferences } = useSelector(state => state.orderCars.userPreferences);
  const { newOffers } = useSelector(state => state.auctions || []);
  const { profile } = useSelector(state => state.user?.defaultUserProfile || []);

  const uf = list => {
    const currentProfile = !!profile ? profile[0] : {};
    return list.filter(
      i => i?.summary?.state?.toUpperCase() === currentProfile?.address?.state?.toUpperCase()
    );
  };

  const km = list => {
    const km = preferences?.find(v => v.name === 'KM').value;
    return list.filter(v => v?.summary?.km <= km);
  };

  const year = list => {
    const year = preferences?.find(v => v.name === 'YEAR').value;
    return list.filter(v => v?.summary?.year >= year);
  };

  const brands = list => {
    // Novo formato: cada grupo é uma preferência separada (`name` = título do grupo, `value: [slugs]`).
    // Exclui os mesmos `name` que a tela de preferências usa na hidratação (evita poluir com outros arrays).
    const brandGroupPrefs = (preferences || []).filter(isLikelyBrandGroupPreferenceEntry);
    const tokensFromGroups = brandGroupPrefs.flatMap(p => p.value).map(String);

    // Legado: um único BRANDS
    const brandsPreference = preferences?.find(v => v.name === 'BRANDS');
    const tokensLegacy = collectBrandFilterTokensFromPreferenceValue(brandsPreference?.value);

    const tokens = [...tokensFromGroups, ...tokensLegacy].filter(Boolean);
    if (!tokens.length) return list;

    const normalizedTokens = new Set(tokens.map(normalizeMakeForFilterCompare));
    const lowerTokens = new Set(tokens.map(t => String(t).toLowerCase()));

    return list.filter(i => {
      const make = i?.summary?.make;
      if (make == null || make === '') return false;
      if (normalizedTokens.has(normalizeMakeForFilterCompare(make))) return true;
      return lowerTokens.has(String(make).toLowerCase());
    });
  };

  // Filtro de leilão - aceita qualquer leilão
  const acceptAuction = list => {
    // Se aceita qualquer leilão, não filtra (retorna todos)
    return list;
  };

  // Filtro de leilão - aceita apenas tipo A
  const acceptAuctionTypeA = list => {
    // Filtrar apenas leilões tipo A
    // Verifica se há menção a "tipo A" no histórico do documento
    return list.filter(auction => {
      const historics = auction?.summary?.documentResearch?.historics || [];
      // Verifica se há menção a tipo A no histórico
      const hasAuctionTypeA = historics.some(historic => {
        const lowerHistoric = String(historic).toLowerCase();
        return (
          lowerHistoric.includes('tipo a') ||
          lowerHistoric.includes('tipo-a') ||
          lowerHistoric.includes('tipoa')
        );
      });
      return hasAuctionTypeA;
    });
  };

  // Filtro de leilão - rejeita leilões
  const rejectAuction = list => {
    // Rejeita apenas leilões que tenham menção a algum tipo de leilão no histórico
    return list.filter(auction => {
      const historics = auction?.summary?.documentResearch?.historics || [];
      // Verifica se há menção a algum tipo de leilão no histórico
      const hasAuctionType = historics.some(historic => {
        const lowerHistoric = String(historic).toLowerCase();
        return lowerHistoric.includes('leilão') || lowerHistoric.includes('leilao');
      });
      // Retorna apenas os que NÃO têm tipo de leilão (rejeita os que têm)
      return !hasAuctionType;
    });
  };

  // Filtro de motor - aceita com apontamentos
  const engineWithIssues = list => {
    // Se aceita com apontamentos, inclui todos (não filtra)
    return list;
  };

  // Filtro de motor - só sem apontamentos (quando engineWithIssues é false)
  const engineNoIssues = list => {
    // Se não aceita com apontamentos, filtra apenas os sem apontamentos
    // canHaveEngineBadge = true significa que NÃO tem apontamentos (pode ter badge)
    return list.filter(
      auction => auction?.vehicle?.canHaveEngineBadge || auction?.summary?.canHaveEngineBadge
    );
  };

  // Filtro de câmbio - aceita com apontamentos
  const transmissionWithIssues = list => {
    // Se aceita com apontamentos, inclui todos (não filtra)
    return list;
  };

  // Filtro de câmbio - só sem apontamentos (quando transmissionWithIssues é false)
  const transmissionNoIssues = list => {
    // Se não aceita com apontamentos, filtra apenas os sem apontamentos
    // canHaveTransmissionBadge = true significa que NÃO tem apontamentos (pode ter badge)
    return list.filter(
      auction =>
        auction?.vehicle?.canHaveTransmissionBadge || auction?.summary?.canHaveTransmissionBadge
    );
  };

  // Filtro de blindado - aceita blindados e comuns
  const armoredAndCommon = list => {
    // Aceita todos (blindados e comuns)
    return list;
  };

  // Filtro de blindado - só blindados
  const onlyArmored = list => {
    return list.filter(
      auction => auction?.summary?.canHaveBlindagemBadge || auction?.vehicle?.canHaveBlindagemBadge
    );
  };

  // Filtro de blindado - não aceita blindados
  const noArmored = list => {
    return list.filter(
      auction =>
        !auction?.summary?.canHaveBlindagemBadge && !auction?.vehicle?.canHaveBlindagemBadge
    );
  };

  const activedPreferences = !!preferences?.length
    ? preferences
        ?.filter(v => v.isActive === true)
        .map(preference => invertPreferencesLabel[preference.name])
    : [];

  const recommendedCars = useMemo(() => {
    let list = newOffers;

    if (!activedPreferences.length || !newOffers.length) return (list = []);

    // Outros filtros existentes (só aplica se isActive === true)
    if (activedPreferences.includes('score80')) list = score80(list);
    if (activedPreferences.includes('cautelar')) list = cautelar(list);
    if (activedPreferences.includes('fipe70')) list = fipe70(list);
    if (activedPreferences.includes('before100k')) list = before100k(list);
    if (activedPreferences.includes('closeToEnd')) list = closeToEnd(list);
    if (activedPreferences.includes('uf')) list = uf(list);
    if (activedPreferences.includes('fipe70k')) list = fipe70k(list);
    if (activedPreferences.includes('fipe100k')) list = fipe100k(list);
    if (activedPreferences.includes('km')) list = km(list);
    if (activedPreferences.includes('year')) list = year(list);
    if (activedPreferences.includes('brands')) list = brands(list);
    if (activedPreferences.includes('acceptAuction')) list = acceptAuction(list);
    if (activedPreferences.includes('acceptAuctionTypeA')) list = acceptAuctionTypeA(list);
    //if (activedPreferences.includes('rejectAuction')) list = rejectAuction(list);
    if (activedPreferences.includes('engineWithIssues')) list = engineWithIssues(list);
    if (activedPreferences.includes('engineNoIssues')) list = engineNoIssues(list);
    if (activedPreferences.includes('transmissionWithIssues')) list = transmissionWithIssues(list);
    if (activedPreferences.includes('transmissionNoIssues')) list = transmissionNoIssues(list);
    if (activedPreferences.includes('armoredAndCommon')) list = armoredAndCommon(list);
    if (activedPreferences.includes('onlyArmored')) list = onlyArmored(list);
    if (activedPreferences.includes('noArmored')) list = noArmored(list);

    return (list = list.map(({ _id }) => _id));
  }, [activedPreferences, newOffers]);

  return { recommendedCars };
};
